import { useContext, useRef } from "react";
import { AppContext } from "./AppContext";
import { AppAction, AppState, ANALYTICS_EVENTS } from "./types";
import { useAnalytics } from "../hooks/use-analytics";

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}

export function useAppState<T>(selector: (state: AppState) => T): T {
  const { state } = useApp();
  return selector(state);
}

export function useAppDispatch() {
  const { dispatch, state } = useApp();
  const { trackEvent } = useAnalytics();
  const recentEvents = useRef<Map<string, number>>(new Map());
  
  return (action: AppAction, skipAnalytics?: boolean) => {
    // Track analytics event if it exists in the map
    const analyticsEvent = ANALYTICS_EVENTS[action.type];
    if (analyticsEvent && !skipAnalytics) {
      const now = Date.now();
      const eventKey = `${analyticsEvent}_${state.currentDocument?.id || 'no-doc'}`;
      const lastTracked = recentEvents.current.get(eventKey);
    
      // Only track if event wasn't tracked within the last second (1000ms)
      if (!lastTracked || now - lastTracked > 1000) {
        const documentId = state.currentDocument?.id;
        const eventProperties: Record<string, string | number | boolean | Date> = {};
        if (documentId) {
            eventProperties.documentId = documentId;
        }
        trackEvent(analyticsEvent, eventProperties);
        recentEvents.current.set(eventKey, now);
        
        // Clean up old entries to prevent memory leaks
        const cutoff = now - 2000; // Keep entries for 2 seconds to be safe
        for (const [key, timestamp] of recentEvents.current.entries()) {
          if (timestamp < cutoff) {
            recentEvents.current.delete(key);
          }
        }
      }
    }
    
    dispatch(action);
  };
} 